/**
 * 我的 AI 服務用量面板（Sprint 4-pre · 使用者自身）。
 * 顯示本月各服務 tokens 用量 / 配額（進度條）+ 近月歷史。任何登入者可看自己。
 * 用於 MyProfilePage 及租戶訂閱頁。
 */
import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, LinearProgress, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  myUsage, myUsageHistory, listAvailableServices, resolveServiceLabel,
  MyUsage, MyUsageHistory, ServiceCode,
} from '../services/subscriptionService';

const fmt = (n: number) => n.toLocaleString();

const MyAiUsagePanel: React.FC = () => {
  const { t, i18n } = useTranslation();
  const tt = (k: string, d: string) => t(`myAiUsage.${k}`, d);
  const [usage, setUsage] = useState<MyUsage | null>(null);
  const [history, setHistory] = useState<MyUsageHistory[]>([]);
  const [registry, setRegistry] = useState<ServiceCode[]>([]);
  const [loading, setLoading] = useState(true);
  const label = (code: string) => resolveServiceLabel(code, registry, i18n.language);

  useEffect(() => {
    let alive = true;
    Promise.all([myUsage(), myUsageHistory(6), listAvailableServices()])
      .then(([u, h, s]) => { if (alive) { setUsage(u); setHistory(h); setRegistry(s); } })
      .catch(() => { /* silent ok: 未訂閱/無用量時面板留白，不干擾 */ })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) return null;
  const services = usage?.services ?? [];

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          💳 {tt('title', '我的 AI 服務用量')}{usage?.month ? `（${usage.month}）` : ''}
        </Typography>
        {services.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {tt('none', '本月尚無 AI 服務用量')}
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {services.map((s) => {
              const pct = s.used_pct ?? 0;
              const unlimited = s.limit === 0;
              return (
                <Box key={s.service_code}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2"><strong>{label(s.service_code)}</strong></Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fmt(s.used_tokens)} {unlimited ? '' : `/ ${fmt(s.limit)}`} tokens · {s.used_calls} calls
                    </Typography>
                  </Stack>
                  {!unlimited && (
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, pct)}
                      color={pct >= 100 ? 'error' : pct >= 80 ? 'warning' : 'success'}
                      sx={{ height: 8, borderRadius: 4, mt: 0.5 }} />
                  )}
                  {unlimited && (
                    <Typography variant="caption" color="text.secondary">{tt('unlimited', '無配額限制')}</Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
        {history.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">{tt('history', '近月用量')}</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
              {history.map((h) => (
                <Box key={h.month} sx={{ px: 1, py: 0.5, background: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="caption">{h.month}: {fmt(h.used_tokens)}</Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default MyAiUsagePanel;
